import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Clock, LogIn, LogOut, MapPin, Download } from "lucide-react";
import { downloadExcel } from "../../lib/excel";

function fmtDuration(mins) {
  if (mins == null) return "—";
  if (mins < 1) return "<1m";
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function Attendance() {
  const today = todayIso();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [data, setData] = useState({ entries: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/attendance", {
        params: { date_from: from, date_to: to },
      });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, []);

  const totalVisits = data.entries.reduce((a, b) => a + b.visit_count, 0);
  const totalHours = (
    data.entries.reduce((a, b) => a + b.total_duration_min, 0) / 60
  ).toFixed(1);
  const openVisits = data.entries.reduce((a, b) => a + b.open_visits, 0);

  const exportCsv = () => {
    const summaryRows = data.entries.map((e) => [
      e.date,
      e.salesman_name || "",
      e.visit_count,
      e.total_duration_min,
      fmtTime(e.first_check_in),
      fmtTime(e.last_check_out),
      e.open_visits,
    ]);
    // Visit-level detail
    const visitRows = [];
    for (const e of data.entries) {
      for (const v of e.visits || []) {
        visitRows.push([
          e.date,
          e.salesman_name || "",
          v.store_name,
          fmtTime(v.check_in_at),
          fmtTime(v.check_out_at),
          v.duration_minutes ?? "",
          v.check_out_at ? "Closed" : "Open",
          v.notes || "",
        ]);
      }
    }
    downloadExcel(`GM_Kridam_Attendance_${from}_to_${to}.xlsx`, [
      {
        name: "Daily summary",
        headers: [
          "Date",
          "Salesman",
          "Visits",
          "Total time (min)",
          "First check-in",
          "Last check-out",
          "Open visits",
        ],
        rows: summaryRows,
      },
      {
        name: "Visit details",
        headers: [
          "Date",
          "Salesman",
          "Store",
          "Check-in",
          "Check-out",
          "Duration (min)",
          "Status",
          "Notes",
        ],
        rows: visitRows,
      },
    ]);
  };

  return (
    <div className="space-y-5" data-testid="admin-attendance-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">Field ops</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Attendance report
          </h1>
          <p className="text-[color:var(--muted)] mt-1">
            Check-ins, check-outs & time-on-store per salesman.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="overline text-[color:var(--muted)]">From</label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-10"
              data-testid="attendance-from-input"
            />
          </div>
          <div>
            <label className="overline text-[color:var(--muted)]">To</label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-10"
              data-testid="attendance-to-input"
            />
          </div>
          <Button
            onClick={load}
            disabled={loading}
            className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white h-10"
            data-testid="attendance-run-button"
          >
            {loading ? "Loading…" : "Run report"}
          </Button>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={data.entries.length === 0}
            className="h-10"
            data-testid="attendance-export-csv"
          >
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total visits" value={totalVisits} icon={LogIn} tone="moss" />
        <Kpi label="Hours on store" value={totalHours} icon={Clock} tone="primary" />
        <Kpi label="Open visits" value={openVisits} icon={MapPin} tone={openVisits > 0 ? "mustard" : "ink"} />
        <Kpi
          label="Active salesmen"
          value={new Set(data.entries.map((e) => e.salesman_id)).size}
          icon={LogOut}
          tone="ink"
        />
      </div>

      <div className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface-2)]">
              <tr className="text-left text-[color:var(--muted)] uppercase text-xs">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Salesman</th>
                <th className="px-4 py-3 text-right">Visits</th>
                <th className="px-4 py-3 text-right">Total time</th>
                <th className="px-4 py-3">First check-in</th>
                <th className="px-4 py-3">Last check-out</th>
                <th className="px-4 py-3 text-right">Open</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e, i) => (
                <tr
                  key={`${e.salesman_id}-${e.date}-${i}`}
                  className="border-t border-[color:var(--border)] hover:bg-[color:var(--surface-2)]/40"
                  data-testid={`attendance-row-${e.salesman_id}-${e.date}`}
                >
                  <td className="px-4 py-3">{e.date}</td>
                  <td className="px-4 py-3 font-medium">{e.salesman_name || "—"}</td>
                  <td className="px-4 py-3 text-right">{e.visit_count}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {fmtDuration(e.total_duration_min)}
                  </td>
                  <td className="px-4 py-3">{fmtTime(e.first_check_in)}</td>
                  <td className="px-4 py-3">{fmtTime(e.last_check_out)}</td>
                  <td className="px-4 py-3 text-right">
                    {e.open_visits > 0 ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[color:var(--mustard)]/20 text-[#9A742E]">
                        {e.open_visits}
                      </span>
                    ) : (
                      <span className="text-[color:var(--muted)]">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setSelected(e)}
                      className="text-xs text-[color:var(--primary)] hover:underline"
                      data-testid={`attendance-view-${e.salesman_id}-${e.date}`}
                    >
                      View details →
                    </button>
                  </td>
                </tr>
              ))}
              {data.entries.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan="8"
                    className="py-12 text-center text-[color:var(--muted)]"
                  >
                    No check-ins recorded in this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="attendance-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {selected?.salesman_name} · {selected?.date}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <MiniStat label="Visits" value={selected.visit_count} />
                <MiniStat
                  label="Time on store"
                  value={fmtDuration(selected.total_duration_min)}
                />
                <MiniStat label="Open" value={selected.open_visits} />
              </div>
              <div>
                <p className="overline mb-2">Visits log</p>
                <ul className="divide-y divide-[color:var(--border)] max-h-80 overflow-y-auto border border-[color:var(--border)] rounded-md">
                  {selected.visits.map((v) => (
                    <li key={v.id} className="p-3 text-sm flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{v.store_name}</div>
                        <div className="text-xs text-[color:var(--muted)]">
                          In {fmtTime(v.check_in_at)} · Out {fmtTime(v.check_out_at)}
                        </div>
                      </div>
                      <div className="tabular-nums text-sm shrink-0">
                        {v.check_out_at
                          ? fmtDuration(v.duration_minutes)
                          : <span className="text-[color:var(--mustard)]">Open</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  const tones = {
    primary: "bg-[color:var(--primary)]/10 text-[color:var(--primary)]",
    moss: "bg-[color:var(--moss)]/10 text-[color:var(--moss)]",
    mustard: "bg-[color:var(--mustard)]/20 text-[#9A742E]",
    ink: "bg-[color:var(--ink)]/5 text-[color:var(--ink)]",
  };
  return (
    <div className="rounded-xl border border-[color:var(--border)] bg-white p-5 ink-shadow">
      <div className="flex items-center justify-between">
        <span className="overline text-[color:var(--muted)]">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-[color:var(--ink)]">{value}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg border border-[color:var(--border)] p-3">
      <div className="overline">{label}</div>
      <div className="font-display text-xl font-bold mt-1">{value}</div>
    </div>
  );
}
